import { Grid } from "@mui/material";
import { Icons } from "assets";
import styles from "pages/manageBanner/style.module.scss";
import React, { useRef, useState } from "react";
import CropImage from "shared/modals/cropImage";

// Banner must be 1920 x 576 (10:3 aspect ratio)
const BANNER_ASPECT_RATIO = 1920 / 576;

interface UploadBannerSectionProps {
  setFieldValue: (field: string, value: any) => void;
  errors: Record<string, any>;
  clickableChecked: boolean;
  touched: Record<string, any>;
  values: any;
  setIsButtonImageAdded: (val: boolean) => void;
  setExternalLinkAdded: any;
}

function UploadBannerSection({
  setFieldValue,
  errors,
  clickableChecked,
  touched,
  values,
  setIsButtonImageAdded,
  setExternalLinkAdded,
}: UploadBannerSectionProps) {
  const [banner, setBanner] = useState<any>({
    raw: "",
    preview: values?.banner_image ? values?.banner_image : "",
  });
  const [buttonImg, setButtonImage] = useState<any>({
    raw: "",
    preview: values?.button_image ? values?.button_image : "",
  });

  const [openCropImage, setOpenCropImage] = useState<boolean>(false);
  const [cropSrc, setCropSrc] = useState<any>("");

  const bannerRef = useRef<HTMLInputElement | null>(null);
  const buttonImgRef = useRef<HTMLInputElement | null>(null);

  const handleImageChange = (e: any, banner: boolean) => {
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    // Banner image must be cropped to the 1920 x 576 ratio first.
    if (banner) {
      setCropSrc(URL.createObjectURL(file));
      setOpenCropImage(true);
      return;
    }

    const reader = new FileReader();

    reader.onload = (ev: any) => {
      setButtonImage({
        preview: ev.target.result as string,
        raw: file,
      });
      setFieldValue("button_image", file);
      setIsButtonImageAdded(true);
    };

    reader.readAsDataURL(file);
  };

  const handleCloseCropImage = () => {
    setOpenCropImage(false);
    setCropSrc("");
    // allow re-selecting the same file to re-open the cropper
    if (bannerRef.current) {
      bannerRef.current.value = "";
    }
  };

  const handleCroppedBanner = (img: any) => {
    setBanner({
      preview: URL.createObjectURL(img),
      raw: img,
    });
    setFieldValue("banner_image", img);
  };

  const handleImageDelete = (banner: boolean) => {
    if (banner) {
      setBanner({ preview: "", raw: null });
      setFieldValue("banner_image", "");
      if (bannerRef.current) {
        bannerRef.current.value = "";
      }
    } else {
      setButtonImage({ preview: "", raw: null });
      if (buttonImgRef.current) {
        buttonImgRef.current.value = "";
      }
      setFieldValue("button_image", "");
      setIsButtonImageAdded(false);
    }
  };

  const handleButtonClick = (banner: boolean) => {
    if (banner) {
      if (bannerRef.current) {
        bannerRef.current.click();
      }
    } else {
      if (buttonImgRef.current) {
        buttonImgRef.current.click();
      }
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={9}>
          <div className={styles.section}>
            <label className={styles.label}>Upload Banner Image</label>
            <div>
              <input
                type="file"
                accept="image/*,image/avif"
                onChange={(e) => handleImageChange(e, true)}
                ref={bannerRef}
                style={{ display: "none" }}
              />
              <div className={styles.imgContainer}>
                {banner?.preview && (
                  <Icons.DeleteIcon
                    className={styles.deleteIcon}
                    onClick={() => handleImageDelete(true)}
                  />
                )}
                {banner.preview ? (
                  <div
                    className={styles.bannerPreviewWrap}
                    onClick={() => handleButtonClick(true)}
                  >
                    <img
                      src={banner.preview && banner.preview}
                      alt={"banner"}
                      className={styles.bannerPreview}
                    />
                    {/* Hover overlay so the user knows the image is clickable */}
                    <span className={styles.changeOverlay}>
                      <Icons.UploadFileIcon />
                      Click to change image
                    </span>
                  </div>
                ) : (
                  <div
                    className={styles.uploadContainer}
                    onClick={() => handleButtonClick(true)}
                  >
                    <Icons.UploadFileIcon />
                    <label>Please Upload banner</label>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.error}>
              {touched?.banner_image && errors?.banner_image
                ? errors?.banner_image.toString()
                : null}
            </div>
          </div>
        </Grid>
        <Grid item xs={12} lg={3}>
          <div
            className={styles.section}
            style={{ height: "100%", justifyContent: "flex-start" }}
          >
            <label className={styles.label}>Upload Button Image</label>
            <div className={styles.buttonUploadFill}>
              <input
                type="file"
                accept="image/*,image/avif"
                onChange={(e) => handleImageChange(e, false)}
                ref={buttonImgRef}
                style={{ display: "none" }}
              />
              <div className={styles.buttonImgBox}>
                {buttonImg?.preview && (
                  <Icons.DeleteIcon
                    className={styles.deleteIcon}
                    onClick={() => handleImageDelete(false)}
                  />
                )}
                {buttonImg.preview ? (
                  <img
                    src={buttonImg.preview && buttonImg.preview}
                    alt={"user"}
                    className={styles.uploadedPicture}
                    style={{ height: "100%" }}
                    onClick={() => handleButtonClick(false)}
                  />
                ) : (
                  <div
                    className={styles.uploadContainer}
                    style={{ height: "100%" }}
                    onClick={() => handleButtonClick(false)}
                  >
                    <Icons.UploadFileIcon />
                    <label>Please Upload button image here</label>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.error}>
              {touched?.button_image && errors?.button_image
                ? errors?.button_image.toString()
                : null}
            </div>
          </div>
        </Grid>
      </Grid>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        <label className={styles.label}>
          Add {!clickableChecked ? "External" : "Internal"} URL
        </label>
        <label className={styles.subLabel}>
          The user will navigate to the given{" "}
          {clickableChecked
            ? "internal link /slug e.g. story_competition, "
            : "external link "}{" "}
          which you will add below (this field is compulsory after adding button
          image)
        </label>
        <input
          className={styles.urlInput}
          placeholder={
            !clickableChecked ? `https://google.com` : `Add internal/slug link`
          }
          value={clickableChecked ? values?.internal_url : values?.external_url}
          onChange={(e) => {
            if (clickableChecked) {
              setFieldValue("internal_url", e.target.value);
            } else {
              setFieldValue("external_url", e.target.value);
              setExternalLinkAdded(
                String(e.target.value)?.length > 0 ? true : false
              );
            }
          }}
        />
        <div className={styles.error}>
          {clickableChecked
            ? touched?.internal_url && errors?.internal_url
              ? errors?.internal_url.toString()
              : null
            : touched?.external_url && errors?.external_url
            ? errors?.external_url.toString()
            : null}
        </div>
      </div>

      <CropImage
        open={openCropImage}
        handleClose={handleCloseCropImage}
        image={cropSrc}
        setImage={setBanner}
        handleImage={handleCroppedBanner}
        aspectRatio={BANNER_ASPECT_RATIO}
        label="Adjust Banner (1920 x 576)"
      />
    </div>
  );
}

export default UploadBannerSection;
